import axios from 'axios'
import { apiGet, apiPost, apiPut, apiDelete } from './api'

const PREFIX = '/awgment-llm-local'
const API_BASE = (import.meta.env.VITE_APP_API_GATEWAY_URL as string) ?? ''
const TOKEN_KEY = 'access_token'

function authHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` }
}

export interface CreateProjectPayload {
  project_name: string
  metadata?: { description?: string;[key: string]: unknown }
  file_types?: string[]
  user_access?: string[]
  priority?: string
  status?: string
}

export interface UpdateProjectPayload {
  metadata?: { description?: string;[key: string]: unknown }
  user_access?: string[]
  priority?: string
  status?: string
}

export const createProjectApi = (payload: CreateProjectPayload) =>
  apiPost(`${PREFIX}/project_definition`, {
    project_name: payload.project_name,
    metadata: payload.metadata ?? {},
    file_types: payload.file_types ?? [],
    user_access: payload.user_access ?? [],
    priority: payload.priority ?? 'Medium',
    status: payload.status ?? 'Active',
  })

export const updateProjectApi = (id: string, payload: UpdateProjectPayload) =>
  apiPut(`${PREFIX}/project_definition/${id}`, {
    ...(payload.metadata !== undefined && { metadata: payload.metadata }),
    ...(payload.user_access !== undefined && { user_access: payload.user_access }),
    ...(payload.priority !== undefined && { priority: payload.priority }),
    ...(payload.status !== undefined && { status: payload.status }),
  })

export const deleteProjectApi = (id: string) =>
  apiDelete(`${PREFIX}/project_definition/${id}`)

// ─── Project Files ──────────────────────────────────────────────────────────

export const getProjectFilesApi = (id: string, params?: Record<string, string>) =>
  apiGet(`${PREFIX}/project/${id}/files`, params)

export const uploadProjectFilesApi = (id: string, formData: FormData) =>
  apiPost(`${PREFIX}/project/${id}/upload_files`, formData)

export const analyzeFilesApi = (id: string, fileIds: string[]) =>
  apiPost(`${PREFIX}/project/${id}/analyze_files`, { file_ids: fileIds })

export const getProjectFileContentApi = (id: string, fileId: string, includeScore = false) =>
  apiGet(`${PREFIX}/project/${id}/file_content`, {
    file_id: fileId,
    include_score: String(true),
  })

export const deleteProjectFileApi = (id: string, fileId: string) =>
  apiDelete(`${PREFIX}/project/${id}/file/${fileId}`)

// ─── Blueprint ──────────────────────────────────────────────────────────────

export const generateProjectBlueprintApi = (id: string, payload: Record<string, unknown>) =>
  apiPost(`${PREFIX}/project/${id}/generate_blueprint`, payload)

export const getProjectBlueprintApi = (id: string, fileId: string, includeScore = true) =>
  apiGet(`${PREFIX}/project/${id}/blueprint/${fileId}`, {
    include_score: String(true),
  })

export const saveBlueprintEditApi = (id: string, fileId: string, updatedBlueprint: string) =>
  apiPut(`${PREFIX}/project/${id}/blueprint/${fileId}`, {
    file_id: fileId,
    updated_blueprint: updatedBlueprint,
  })

export const suggestBlueprintEditsApi = (id: string, fileId: string, updatedBlueprint: string) =>
  apiPost(`${PREFIX}/project/${id}/suggest_blueprint_edits`, {
    file_id: fileId,
    updated_blueprint: updatedBlueprint,
  })

// ─── Build ──────────────────────────────────────────────────────────────────

export const transformProjectFileApi = (
  id: string,
  payload: { file_id: string; source_lang: string; target_lang: string; instruction?: string }
) =>
  apiPost(`${PREFIX}/project/${id}/transform_file`, {
    file_id: payload.file_id,
    source_lang: payload.source_lang,
    target_lang: payload.target_lang,
    instruction: payload.instruction ?? '',
  })

export const getProjectTransformedCodeApi = (id: string, fileId: string, includeScore = true) =>
  apiGet(`${PREFIX}/project/${id}/transformed_code/${fileId}`, {
    include_score: String(true),
  })

export const getProjectQualityCompareApi = (
  id: string,
  params?: Record<string, string | number>
) => apiGet(`${PREFIX}/project/${id}/quality_compare`, params)

export const analyzeCodeQualityApi = (payload: {
  code?: string
  file_id?: string
  project_id?: string
}) => apiPost(`${PREFIX}/analyze_code_quality`, payload as Record<string, unknown>)

// ─── Validation ─────────────────────────────────────────────────────────────

export const runTestsApi = (
  id: string,
  payload: { file_id: string; test_code?: string; target_lang?: string }
) =>
  apiPost(`${PREFIX}/project/${id}/run_tests`, {
    file_id: payload.file_id,
    ...(payload.test_code !== undefined && { test_code: payload.test_code }),
    ...(payload.target_lang !== undefined && { target_lang: payload.target_lang }),
  })

export const getTestResultsApi = (id: string, fileId?: string) =>
  apiGet(`${PREFIX}/project/${id}/test_results`, fileId ? { file_id: fileId } : undefined)

// ─── Export ─────────────────────────────────────────────────────────────────

/** Download ZIP of all transformed files — returns a blob */
export const exportProjectZipApi = async (id: string) => {
  try {
    const response = await axios.get(`${API_BASE}${PREFIX}/project/${id}/export_zip`, {
      headers: authHeaders(),
      responseType: 'blob',
    })
    return { success: true, data: response.data as Blob }
  } catch (error: unknown) {
    const msg =
      (error as any)?.response?.data?.message ??
      (error as any)?.message ??
      'Export failed'
    return { success: false, message: msg }
  }
}

export const pushToGitApi = (
  id: string,
  payload: { repo_url: string; branch?: string; commit_msg?: string }
) =>
  apiPost(`${PREFIX}/project/${id}/push_to_git`, {
    repo_url: payload.repo_url,
    ...(payload.branch !== undefined && { branch: payload.branch }),
    ...(payload.commit_msg !== undefined && { commit_msg: payload.commit_msg }),
  })

export const getExportStatsApi = (id: string) =>
  apiGet(`${PREFIX}/project/${id}/export_stats`)

// ─── Source-Target ──────────────────────────────────────────────────────────

/** Generate (or regenerate) source-target code for a file */
export const generateSourceTargetApi = (projectId: string, fileId: string) =>
  apiPost(`${PREFIX}/project/${projectId}/generate_source_target`, { file_id: fileId })

/** Fetch existing source-target code for a file (returns null data if not generated yet) */
export const getSourceTargetApi = (projectId: string, fileId: string) =>
  apiGet(`${PREFIX}/project/${projectId}/source_target/${fileId}?include_score=true`)

// ─── Legacy / UPC asset endpoints (kept for backward compat) ────────────────

export const getKaPreviewMetaApi = (assetId: string, includeScore = true) =>
  apiGet(`${PREFIX}/ka_preview_meta`, {
    asset_id: assetId,
    include_score: String(true),
  })
